import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { FileText, Sheet, Plus, Edit3, RotateCcw, Mail, Send, Filter, X, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";

function DeleteRowBtn({ onClick, testid }) {
  return (
    <button type="button" onClick={onClick} title="Elimina" className="rounded-sm p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" data-testid={testid}>
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}


export default function BefPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [technicians, setTechnicians] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [tech, setTech] = useState("");
  const today = new Date();
  // Costruzione data locale in ISO (YYYY-MM-DD) senza passare da toISOString (che è UTC e sfasa il giorno)
  const pad2 = (n) => String(n).padStart(2, "0");
  const localIso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const firstDay = localIso(new Date(today.getFullYear(), today.getMonth(), 1));
  const lastDay = localIso(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(lastDay);
  const [summary, setSummary] = useState(null);

  // Filtro committenti (multi-select)
  const [committenti, setCommittenti] = useState([]);
  const [selectedCommittenti, setSelectedCommittenti] = useState([]);
  useEffect(() => {
    api.get("/committenti").then((r) => setCommittenti(r.data || [])).catch(() => setCommittenti([]));
  }, []);
  const toggleCommittente = (id) => {
    setSelectedCommittenti((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const commLabel = selectedCommittenti.length === 0
    ? "Tutti i committenti"
    : selectedCommittenti.length === 1
      ? committenti.find((c) => c.id === selectedCommittenti[0])?.name || "1 selezionato"
      : `${selectedCommittenti.length} committenti`;

  // IVA su materiale consegnato
  const [applyIva, setApplyIva] = useState(false);
  const [ivaPercent, setIvaPercent] = useState(22);

  const [chgOpen, setChgOpen] = useState(false);
  const [chg, setChg] = useState({ material_id: "", description: "", quantity: 1, unit_price: 0, amount: 0, date: localIso(new Date()), notes: "" });

  // Invio Email BEF
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [smtpAccountId, setSmtpAccountId] = useState("");
  useEffect(() => {
    api.get("/smtp-accounts").then((r) => setSmtpAccounts(r.data || [])).catch(() => setSmtpAccounts([]));
  }, []);

  // ===== BEF Subappalto: settings persistite (sezioni + override + extra items) =====
  const [subSections, setSubSections] = useState({ lavorazioni: true, charges: true, supplies: true, aggregate: true, discounts: false });
  const [subRigaOv, setSubRigaOv] = useState({}); // key: "<lav_id>__<idx>"
  const [subChargeOv, setSubChargeOv] = useState({});
  const [subSupplyOv, setSubSupplyOv] = useState({});
  const [subExtras, setSubExtras] = useState([]); // [{id,name,amount}]
  const [subSettingsLoaded, setSubSettingsLoaded] = useState(false);
  const [subSaveBusy, setSubSaveBusy] = useState(false);
  const [subExpanded, setSubExpanded] = useState({}); // {technician_id: true}
  const [subDirty, setSubDirty] = useState(false); // ci sono modifiche non salvate
  const isSubappalto = summary?.is_subappalto;
  const currentTech = technicians.find((x) => x.id === tech);
  const isCurrentSub = currentTech?.role === "subappalto";

  const loadSubSettings = async () => {
    if (!tech || !isCurrentSub) return;
    try {
      const { data } = await api.get("/bef/subappalto-settings", {
        params: {
          subappalto_id: tech,
          date_from: dateFrom,
          date_to: dateTo,
          ...(selectedCommittenti.length > 0 ? { committente_ids: selectedCommittenti.join(",") } : {}),
        },
      });
      setSubSections(data.sections || { lavorazioni: true, charges: true, supplies: true, aggregate: true, discounts: false });
      setSubRigaOv(data.riga_overrides || {});
      setSubChargeOv(data.charge_overrides || {});
      setSubSupplyOv(data.supply_overrides || {});
      setSubExtras(data.extra_items || []);
      setSubSettingsLoaded(true);
      setSubDirty(false);
    } catch {
      setSubSettingsLoaded(true);
      setSubDirty(false);
    }
  };

  useEffect(() => {
    if (isCurrentSub) {
      setSubSettingsLoaded(false);
      loadSubSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tech, dateFrom, dateTo, selectedCommittenti, isCurrentSub]);

  const saveSubSettings = async (opts = {}) => {
    if (!isCurrentSub) return;
    setSubSaveBusy(true);
    try {
      await api.put("/bef/subappalto-settings", {
        subappalto_id: tech,
        date_from: dateFrom,
        date_to: dateTo,
        committente_ids: selectedCommittenti.length > 0 ? selectedCommittenti : null,
        sections: subSections,
        riga_overrides: subRigaOv,
        charge_overrides: subChargeOv,
        supply_overrides: subSupplyOv,
        extra_items: subExtras,
      });
      if (!opts.silent) toast.success("Modifiche BEF salvate");
      setSubDirty(false);
      await loadSummary();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
      return false;
    } finally {
      setSubSaveBusy(false);
    }
  };

  const toggleSubSection = (k) => { setSubSections((s) => ({ ...s, [k]: !s[k] })); setSubDirty(true); };

  const setRigaOverride = (lavId, idx, patch) => {
    const key = `${lavId}__${idx}`;
    setSubDirty(true);
    setSubRigaOv((prev) => {
      const cur = prev[key] || {};
      const next = { ...cur, ...patch };
      // Rimuovi la chiave se torna a "default" (null values)
      const cleaned = {};
      Object.entries(next).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") cleaned[k] = v; });
      if (Object.keys(cleaned).length === 0) {
        const copy = { ...prev }; delete copy[key]; return copy;
      }
      return { ...prev, [key]: cleaned };
    });
  };
  const resetRigaOverride = (lavId, idx) => {
    const key = `${lavId}__${idx}`;
    setSubDirty(true);
    setSubRigaOv((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
  };
  const setChargeOverride = (chargeId, patch) => {
    setSubDirty(true);
    setSubChargeOv((prev) => {
      const cur = prev[chargeId] || {};
      const next = { ...cur, ...patch };
      const cleaned = {};
      Object.entries(next).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") cleaned[k] = v; });
      if (Object.keys(cleaned).length === 0) { const copy = { ...prev }; delete copy[chargeId]; return copy; }
      return { ...prev, [chargeId]: cleaned };
    });
  };
  const resetChargeOverride = (chargeId) => { setSubDirty(true); setSubChargeOv((prev) => { const copy = { ...prev }; delete copy[chargeId]; return copy; }); };
  const setSupplyOverride = (supplyId, patch) => {
    setSubDirty(true);
    setSubSupplyOv((prev) => {
      const cur = prev[supplyId] || {};
      const next = { ...cur, ...patch };
      const cleaned = {};
      Object.entries(next).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") cleaned[k] = v; });
      if (Object.keys(cleaned).length === 0) { const copy = { ...prev }; delete copy[supplyId]; return copy; }
      return { ...prev, [supplyId]: cleaned };
    });
  };
  const resetSupplyOverride = (supplyId) => { setSubDirty(true); setSubSupplyOv((prev) => { const copy = { ...prev }; delete copy[supplyId]; return copy; }); };

  const addExtraItem = () => {
    setSubDirty(true);
    setSubExtras((prev) => [...prev, { id: `x_${Date.now()}`, name: "", amount: 0 }]);
  };
  const updateExtraItem = (id, patch) => {
    setSubDirty(true);
    setSubExtras((prev) => prev.map((x) => x.id === id ? { ...x, ...patch } : x));
  };
  const removeExtraItem = (id) => {
    setSubDirty(true);
    setSubExtras((prev) => prev.filter((x) => x.id !== id));
  };

  // Deriva "competenza {mese} {anno}" o "dal ... al ..." dai filtri per anteprima oggetto
  const MESI_IT = ["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"];
  const competenzaLabel = (() => {
    try {
      const [y1, m1] = dateFrom.split("-");
      const [y2, m2] = dateTo.split("-");
      if (y1 === y2 && m1 === m2) return `competenza ${MESI_IT[parseInt(m1) - 1]} ${y1}`;
      return `competenza dal ${dateFrom.slice(8,10)}/${dateFrom.slice(5,7)}/${dateFrom.slice(0,4)} al ${dateTo.slice(8,10)}/${dateTo.slice(5,7)}/${dateTo.slice(0,4)}`;
    } catch { return `competenza dal ${dateFrom} al ${dateTo}`; }
  })();

  useEffect(() => {
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => {
      setTechnicians(r.data);
      if (r.data.length && !tech) setTech(r.data[0].id);
    });
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  }, []);

  const pickMaterial = (materialId) => {
    const m = materials.find((x) => x.id === materialId);
    if (!m) {
      setChg((c) => ({ ...c, material_id: "" }));
      return;
    }
    const unitPrice = Number(m.unit_price ?? 0);
    const qty = Number(chg.quantity) || 1;
    setChg((c) => ({
      ...c,
      material_id: m.id,
      description: c.description && c.description.length ? c.description : m.name,
      unit_price: unitPrice,
      amount: +(unitPrice * qty).toFixed(2),
    }));
  };

  const updateQty = (v) => {
    const qty = Number(v) || 0;
    setChg((c) => ({ ...c, quantity: qty, amount: +((Number(c.unit_price) || 0) * qty).toFixed(2) }));
  };

  const updateUnitPrice = (v) => {
    const up = Number(v) || 0;
    setChg((c) => ({ ...c, unit_price: up, amount: +((Number(c.quantity) || 0) * up).toFixed(2) }));
  };

  const overridesPayload = () => undefined;

  const deleteCharge = async (c) => {
    if (!window.confirm(`Eliminare l'addebito "${c.description}" di € ${Number(c.amount).toFixed(2)}?`)) return;
    try { await api.delete(`/charges/${c.id}`); toast.success("Addebito eliminato"); loadSummary(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eliminazione non riuscita"); }
  };
  const deleteSupply = async (s) => {
    if (!window.confirm(`Eliminare la consegna "${s.material_name}" (${s.quantity})? La quantità torna in giacenza nel magazzino.`)) return;
    try { await api.delete(`/technician-supplies/${s.id}`); toast.success("Consegna eliminata"); loadSummary(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eliminazione non riuscita"); }
  };

  const loadSummary = async () => {
    if (!tech) return;
    try {
      const { data } = await api.get("/bef/summary", { params: { technician_id: tech, date_from: dateFrom, date_to: dateTo, apply_iva: applyIva, iva_percent: ivaPercent, ...(selectedCommittenti.length > 0 ? { committente_ids: selectedCommittenti.join(",") } : {}) } });
      setSummary(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { loadSummary(); }, [tech, dateFrom, dateTo, applyIva, ivaPercent, selectedCommittenti]);

  const saveSupplyPrice = async (supplyId, newUnitPrice) => {
    const v = Number(newUnitPrice);
    if (isNaN(v) || v < 0) return;
    try {
      await api.patch(`/technician-supplies/${supplyId}/unit-price`, { unit_price: v });
      toast.success(t("auto.prezzo_salvato_per_questa_consegna"));
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio prezzo");
    }
  };

  const resetSupplyPrice = async (supplyId) => {
    try {
      await api.post(`/technician-supplies/${supplyId}/reset-price`);
      toast.success(t("auto.prezzo_ripristinato_dal_listino"));
      loadSummary();    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore reset");
    }
  };

  const toggleSupplyCharge = async (supplyId, charged) => {
    try {
      await api.patch(`/technician-supplies/${supplyId}/charge`, { charge: !charged });
      toast.success(charged ? "Materiale non addebitato" : "Materiale di nuovo addebitato");
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore aggiornamento");
    }
  };

  const toggleSupplyIva = async (supplyId, currentEffective) => {
    // Toggle: se currentEffective vero → forza a false, altrimenti → forza a true
    try {
      await api.patch(`/technician-supplies/${supplyId}/apply-iva`, { apply_iva: !currentEffective });
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore aggiornamento IVA");
    }
  };

  // Modifica manuale del totale di una lavorazione (override che sovrascrive somma righe)
  const overrideLavorazioneTotal = async (lavId, currentTotal) => {
    const input = window.prompt("Inserisci il nuovo importo (€) per questa lavorazione:", String(currentTotal.toFixed(2)));
    if (input === null) return;
    const v = Number(String(input).replace(",", "."));
    if (isNaN(v) || v < 0) return toast.error("Importo non valido");
    try {
      await api.patch(`/lavorazioni/${lavId}/total-override`, { total_override: v });
      toast.success("Importo modificato ✓");
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore modifica importo");
    }
  };

  const resetLavorazioneTotal = async (lavId) => {
    if (!window.confirm("Ripristinare l'importo originale (somma delle righe)?")) return;
    try {
      await api.patch(`/lavorazioni/${lavId}/total-override`, { total_override: null });
      toast.success("Importo ripristinato dalle righe");
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore reset");
    }
  };

  // Override NETTO (post-sconto) di una lavorazione: bypassa lo sconto automatico.
  const overrideLavorazioneNet = async (lavId, currentNet) => {
    const input = window.prompt("Inserisci il nuovo NETTO (€) post-sconto per questa lavorazione.\nBypassa lo sconto automatico del tecnico.", String(Number(currentNet || 0).toFixed(2)));
    if (input === null) return;
    const v = Number(String(input).replace(",", "."));
    if (isNaN(v) || v < 0) return toast.error("Importo non valido");
    try {
      await api.patch(`/lavorazioni/${lavId}/net-override`, { net_override: v });
      toast.success("Netto modificato ✓");
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore modifica netto");
    }
  };

  const resetLavorazioneNet = async (lavId) => {
    if (!window.confirm("Ripristinare il netto calcolato automaticamente (lordo × sconto)?")) return;
    try {
      await api.patch(`/lavorazioni/${lavId}/net-override`, { net_override: null });
      toast.success("Netto ripristinato");
      loadSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore reset");
    }
  };

  const downloadFile = async (kind) => {
    if (!tech) return;
    // Auto-save modifiche subappalto se ci sono override/extra non ancora salvati
    if (isCurrentSub && subDirty) {
      const ok = await saveSubSettings({ silent: true });
      if (!ok) {
        toast.error("Impossibile salvare le modifiche prima dell'export");
        return;
      }
      toast.info("Modifiche salvate e applicate al file");
    }
    try {
      const res = await api.get(`/bef/${kind}`, { params: { technician_id: tech, date_from: dateFrom, date_to: dateTo, apply_iva: applyIva, iva_percent: ivaPercent, ...(selectedCommittenti.length > 0 ? { committente_ids: selectedCommittenti.join(",") } : {}) }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const ext = kind === "pdf" ? "pdf" : "xlsx";
      // Prova a leggere il nome file dal Content-Disposition (contiene già il nome del tecnico)
      let filename = "";
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m) filename = m[1];
      }
      if (!filename) {
        const tName = (technicians.find((x) => x.id === tech)?.name || "collaboratore").replace(/\s+/g, "_");
        filename = `BEF_${tName}_${dateFrom}_${dateTo}.${ext}`;
      }
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore export"); }
  };

  const openSendEmail = () => {
    if (!tech) return toast.error("Seleziona il collaboratore");
    const t = technicians.find((x) => x.id === tech);
    setEmailTo(t?.email || "");
    setEmailOpen(true);
  };

  const sendBefEmail = async () => {
    if (!tech) return toast.error("Seleziona il collaboratore");
    if (!emailTo || !emailTo.includes("@")) return toast.error("Inserisci un'email valida");
    // Auto-save modifiche subappalto prima dell'invio email
    if (isCurrentSub && subDirty) {
      const ok = await saveSubSettings({ silent: true });
      if (!ok) {
        toast.error("Impossibile salvare le modifiche prima dell'invio");
        return;
      }
    }
    setEmailBusy(true);
    try {
      const { data } = await api.post("/bef/send-email", {
        technician_id: tech,
        date_from: dateFrom,
        date_to: dateTo,
        apply_iva: applyIva,
        iva_percent: ivaPercent,
        to: emailTo,
        smtp_account_id: smtpAccountId || undefined,
        committente_ids: selectedCommittenti.length > 0 ? selectedCommittenti : undefined,
      });
      toast.success(`Email inviata a ${data.to}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio email");
    } finally {
      setEmailBusy(false);
    }
  };

  const addCharge = async () => {
    if (!tech || !chg.description || !chg.amount) return toast.error("Compila descrizione e importo");
    try {
      await api.post("/charges", {
        technician_id: tech,
        date: chg.date,
        description: chg.description,
        amount: Number(chg.amount),
        notes: chg.notes,
      });
      toast.success("Addebito aggiunto");
      setChgOpen(false);
      setChg({ material_id: "", description: "", quantity: 1, unit_price: 0, amount: 0, date: localIso(new Date()), notes: "" });
      loadSummary();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.consuntivazione_kicker")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.bef")}</h1>
        </div>
        <a href="/bef-manuali" className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="btn-bef-manuali">
          <Plus className="h-4 w-4" /> {t("auto.nuovo_bef_manuale_da_zero")}
        </a>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <div>
            <Label>{t("auto.tecnico")}</Label>
            <Select value={tech} onValueChange={setTech}>
              <SelectTrigger className="rounded-sm" data-testid="bef-tech"><SelectValue /></SelectTrigger>
              <SelectContent>{technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>{t("auto.dal")}</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm" data-testid="bef-from" /></div>
          <div><Label>{t("auto.al")}</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm" data-testid="bef-to" /></div>
          <div>
            <Label className="flex items-center gap-1">
              Committenti
              {selectedCommittenti.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedCommittenti([])}
                  className="ml-1 text-slate-400 hover:text-slate-700"
                  data-testid="bef-clear-committenti"
                  title="Rimuovi filtro committenti"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 w-full justify-between rounded-sm bg-white font-normal"
                  data-testid="bef-committenti-trigger"
                >
                  <span className="truncate">
                    <Filter className="mr-1.5 inline h-3.5 w-3.5" />
                    {commLabel}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 rounded-sm p-2" align="start">
                <div className="mb-1 px-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Seleziona uno o più committenti
                </div>
                <div className="max-h-60 space-y-1 overflow-auto">
                  {committenti.length === 0 && (
                    <div className="p-2 text-xs text-slate-400">Nessun committente</div>
                  )}
                  {committenti.map((c) => (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-slate-100"
                      data-testid={`bef-committente-opt-${c.id}`}
                    >
                      <Checkbox
                        checked={selectedCommittenti.includes(c.id)}
                        onCheckedChange={() => toggleCommittente(c.id)}
                      />
                      <span className="text-sm">{c.name}</span>
                    </label>
                  ))}
                </div>
                {selectedCommittenti.length > 0 && (
                  <div className="mt-2 border-t border-slate-200 pt-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedCommittenti([])}
                      className="h-7 w-full rounded-sm text-xs"
                      data-testid="bef-committenti-reset"
                    >
                      Reset ({selectedCommittenti.length} selezionati)
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Button onClick={() => downloadFile("pdf")} variant="outline" className="rounded-sm" data-testid="bef-pdf"><FileText className="mr-2 h-4 w-4" /> PDF</Button>
            <Button onClick={() => downloadFile("excel")} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="bef-excel"><Sheet className="mr-2 h-4 w-4" /> Excel</Button>
            <Button onClick={openSendEmail} className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700" data-testid="bef-send-email">
              <Mail className="mr-2 h-4 w-4" /> Invia Email
            </Button>
          </div>
        </div>
        {selectedCommittenti.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-slate-500">Filtro attivo:</span>
            {selectedCommittenti.map((cid) => {
              const c = committenti.find((x) => x.id === cid);
              return (
                <span
                  key={cid}
                  className="inline-flex items-center gap-1 rounded-sm border border-[#0052FF] bg-[#0052FF]/10 px-2 py-0.5 font-medium text-[#0052FF]"
                  data-testid={`bef-committente-chip-${cid}`}
                >
                  {c?.name || cid}
                  <button
                    type="button"
                    onClick={() => toggleCommittente(cid)}
                    className="hover:text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-sm border border-slate-200 bg-slate-50 p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-800" title={t("auto.default_iva_per_le_nuove_righe_ogni_riga_può_essere_impostat")}>
            <input
              type="checkbox"
              checked={applyIva}
              onChange={(e) => setApplyIva(e.target.checked)}
              className="h-4 w-4 rounded-sm accent-[#0052FF]"
              data-testid="bef-apply-iva"
            />
            IVA <b>{t("auto.predefinita")}</b> {t("auto.per_il_materiale_usa_i_flag_riga_per_riga_per_un_controllo_p")}
          </label>
          {applyIva && (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={ivaPercent}
                onChange={(e) => setIvaPercent(Number(e.target.value) || 0)}
                className="h-8 w-20 rounded-sm font-mono text-sm"
                data-testid="bef-iva-percent"
              />
              <span className="text-sm text-slate-600">%</span>
            </div>
          )}
          {applyIva && summary && (
            <span className="text-xs text-slate-500">
              {t("auto.iva_calcolata_hint")} <span className="font-mono font-semibold text-slate-800">€ {summary.totali.supplies_imponibile.toFixed(2)}</span> → IVA <span className="font-mono font-semibold text-slate-800">€ {summary.totali.supplies_iva.toFixed(2)}</span>
            </span>
          )}
        </div>
      </div>

      {summary && summary.is_subappalto && (
        <>
          {/* Toggle sezioni + Save */}
          <div className={`rounded-sm border p-5 ${subDirty ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Sezioni da includere in PDF / Excel</div>
                  {subDirty && (
                    <span className="rounded-sm border border-amber-400 bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-900" data-testid="bef-sub-dirty-badge">
                      ● Modifiche non salvate
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  Spunta le sezioni che vuoi allegare. {subDirty ? <b className="text-amber-700">Clicca "Salva modifiche" o esporta: verrà salvato in automatico.</b> : "Le modifiche sono salvate per questo BEF (subappalto + periodo)."}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => saveSubSettings()}
                  disabled={subSaveBusy || !subDirty}
                  className={`rounded-sm ${subDirty ? "bg-amber-600 text-white hover:bg-amber-700" : "bg-slate-300 text-slate-500"}`}
                  data-testid="bef-sub-save"
                >
                  {subSaveBusy ? "Salvo…" : (subDirty ? "Salva modifiche" : "Tutto salvato ✓")}
                </Button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              {[
                ["aggregate", "Riepilogo aggregato per tecnico"],
                ["discounts", "Mostra scontistiche (Lordo → Sconto → Netto)"],
                ["lavorazioni", "Dettaglio lavorazioni"],
                ["charges", "Dettaglio addebiti"],
                ["supplies", "Dettaglio materiale consegnato"],
              ].map(([k, label]) => (
                <label key={k} className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!subSections[k]}
                    onChange={() => toggleSubSection(k)}
                    className="h-4 w-4 rounded-sm accent-[#0052FF]"
                    data-testid={`bef-sub-section-${k}`}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="kpi-card">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Totale Lordo Produzione</div>
              <div className="mt-2 font-mono text-xl font-semibold text-slate-900">€ {summary.totali.lordo.toFixed(2)}</div>
            </div>
            <div className="kpi-card">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Netto lavorazioni</div>
              <div className="mt-2 font-mono text-xl font-semibold text-slate-900">€ {Number(summary.totali.netto_before_extra ?? summary.totali.netto).toFixed(2)}</div>
            </div>
            <div className="kpi-card">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Voci Extra</div>
              <div className={`mt-2 font-mono text-xl font-semibold ${Number(summary.totali.extra_total || 0) >= 0 ? "text-slate-900" : "text-red-600"}`}>
                {Number(summary.totali.extra_total || 0) >= 0 ? "+" : ""}€ {Number(summary.totali.extra_total || 0).toFixed(2)}
              </div>
            </div>
            <div className="kpi-card">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">BEF Subappalto</div>
              <div className="mt-2 font-mono text-xl font-semibold text-emerald-600">€ {summary.totali.netto.toFixed(2)}</div>
            </div>
          </div>

          {/* Voci extra manuali */}
          <div className="rounded-sm border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Voci Extra Manuali (Costo Cedolino, ecc.)</div>
              <Button size="sm" variant="outline" onClick={addExtraItem} className="rounded-sm" data-testid="bef-sub-add-extra">
                <Plus className="mr-1 h-3 w-3" /> Aggiungi voce
              </Button>
            </div>
            <div className="mb-2 text-[11px] text-slate-500">Usa valori negativi per <b>sottrarre</b> (es. Costo Cedolino: -150) o positivi per <b>aggiungere</b> (es. Bonus: 300). Salva per applicare.</div>
            {subExtras.length === 0 && (
              <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-400">Nessuna voce extra. Clicca "Aggiungi voce" per inserire una voce manuale.</div>
            )}
            {subExtras.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-2">Descrizione</th>
                    <th className="p-2 text-right">Importo € (+/-)</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {subExtras.map((ei) => (
                    <tr key={ei.id} className="border-b border-slate-100" data-testid={`bef-sub-extra-${ei.id}`}>
                      <td className="p-2">
                        <Input
                          value={ei.name || ""}
                          onChange={(e) => updateExtraItem(ei.id, { name: e.target.value })}
                          placeholder="Es. Costo Cedolino"
                          className="h-8 rounded-sm text-sm"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Input
                          type="text"
                          inputMode="decimal"
                          defaultValue={ei.amount === 0 ? "" : String(ei.amount)}
                          key={`ex-amt-${ei.id}`}
                          placeholder="es. -150 oppure 300"
                          onChange={(e) => {
                            // Aggiorna dirty subito anche durante la digitazione (per il badge)
                            const raw = e.target.value;
                            // Permetti: "-", "", "-.", "1.5", "-4700.5" ecc
                            if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
                              // Ancora incompleto: non aggiornare amount ma segna dirty
                              setSubDirty(true);
                              return;
                            }
                            const v = Number(raw.replace(",", "."));
                            if (!isNaN(v)) updateExtraItem(ei.id, { amount: v });
                          }}
                          onBlur={(e) => {
                            const raw = e.target.value;
                            if (raw === "" || raw === "-" || raw === "." || raw === "-.") {
                              updateExtraItem(ei.id, { amount: 0 });
                              e.target.value = "0";
                              return;
                            }
                            const v = Number(raw.replace(",", "."));
                            updateExtraItem(ei.id, { amount: isNaN(v) ? 0 : v });
                          }}
                          className="h-8 w-32 rounded-sm text-right font-mono text-sm"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => removeExtraItem(ei.id)}
                          className="text-slate-400 hover:text-red-600"
                          title="Rimuovi voce"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Riepilogo aggregato per tecnico (visibile sempre in UI, il toggle influenza solo PDF/Excel) */}
          <div className="rounded-sm border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Riepilogo per Tecnico Associato</div>
              <div className="text-[11px] text-slate-500">{subSections.aggregate ? "Incluso in PDF/Excel" : "Escluso da PDF/Excel"}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-2">Tecnico</th>
                    <th className="p-2">Modalità</th>
                    <th className="p-2 text-right">Lordo</th>
                    <th className="p-2 text-right">Sconto 1 (sul lordo)</th>
                    <th className="p-2 text-right">Margine</th>
                    <th className="p-2 text-right">Sconto 2 (sul margine)</th>
                    <th className="p-2 text-right">Materiale/Spese</th>
                    <th className="p-2 text-right">Netto Subappalto</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary.rows || []).length === 0 && (
                    <tr><td colSpan={8} className="p-6 text-center text-slate-400">Nessun tecnico associato a questo subappalto</td></tr>
                  )}
                  {(summary.rows || []).map((r) => (
                    <tr key={r.technician_id} className="border-b border-slate-100" data-testid={`bef-sub-row-${r.technician_id}`}>
                      <td className="p-2 font-medium">{r.technician_name}</td>
                      <td className="p-2">
                        <span className={`rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase ${r.mode === "commissione" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>
                          {r.mode === "commissione" ? "Commissione" : "Completo"}
                        </span>
                      </td>
                      <td className="p-2 text-right font-mono">€ {Number(r.lordo).toFixed(2)}</td>
                      <td className="p-2 text-right font-mono text-orange-600">{r.sconto_percent}% (- € {Number(r.sconto_amount).toFixed(2)})</td>
                      <td className="p-2 text-right font-mono">
                        {r.mode === "commissione" ? `€ ${Number(r.sconto_amount).toFixed(2)}` : `€ ${Number(r.scontato).toFixed(2)}`}
                      </td>
                      <td className="p-2 text-right font-mono text-blue-600">
                        {r.mode === "commissione" ? `${r.sconto2_percent}% (- € ${Number(r.sconto2_amount).toFixed(2)})` : "—"}
                      </td>
                      <td className="p-2 text-right font-mono text-red-600">
                        {Number(r.materiale || 0) > 0 ? `- € ${Number(r.materiale).toFixed(2)}` : "—"}
                      </td>
                      <td className="p-2 text-right font-mono font-bold text-emerald-600">€ {Number(r.netto).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dettaglio completo per tecnico associato (lavorazioni, addebiti, materiale) */}
          {(summary.techs_detail || []).map((td) => {
            const expanded = subExpanded[td.technician_id] !== false; // di default aperto
            const totRig = Object.keys(subRigaOv).filter((k) => (summary.techs_detail || []).find((x) => x.technician_id === td.technician_id)?.lavorazioni.some((l) => k.startsWith(`${l.id}__`))).length;
            const totCh = td.charges.filter((c) => !!subChargeOv[c.id]).length;
            const totSup = td.supplies.filter((s) => !!subSupplyOv[s.id]).length;
            const ovBadge = totRig + totCh + totSup;
            return (
              <div key={td.technician_id} className="rounded-sm border border-slate-200 bg-white p-5" data-testid={`bef-sub-tech-${td.technician_id}`}>
                <button
                  type="button"
                  onClick={() => setSubExpanded((p) => ({ ...p, [td.technician_id]: expanded ? false : true }))}
                  className="mb-3 flex w-full items-center justify-between text-left"
                >
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-bold text-slate-900">{td.technician_name}</div>
                    <span className={`rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase ${td.mode === "commissione" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>
                      {td.mode === "commissione" ? "Commissione" : "Completo"}
                    </span>
                    {ovBadge > 0 && (
                      <span className="rounded-sm border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">
                        {ovBadge} modifiche
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-slate-400">{expanded ? "▲ Nascondi dettagli" : "▼ Mostra dettagli"}</span>
                </button>

                {expanded && (
                  <div className="space-y-4">
                    {/* Lavorazioni */}
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Lavorazioni</div>
                        <div className="text-[11px] text-slate-400">{subSections.lavorazioni ? "Incluse in PDF/Excel" : "Escluse"}</div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="border-b border-slate-200 bg-slate-50 text-left font-bold uppercase tracking-widest text-slate-500">
                            <tr>
                              <th className="p-1.5">Data</th>
                              <th className="p-1.5">Committente</th>
                              <th className="p-1.5">Impianto</th>
                              <th className="p-1.5">Codice</th>
                              <th className="p-1.5">Descrizione</th>
                              <th className="p-1.5 text-right">Q.tà</th>
                              <th className="p-1.5 text-right">Prezzo unit. €</th>
                              <th className="p-1.5 text-right">Totale</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(td.lavorazioni || []).length === 0 && (
                              <tr><td colSpan={8} className="p-4 text-center text-slate-400">Nessuna lavorazione nel periodo</td></tr>
                            )}
                            {(td.lavorazioni || []).flatMap((l) => (l.righe || []).map((r) => {
                              const key = `${l.id}__${r.index}`;
                              const ov = subRigaOv[key] || {};
                              const hasOv = r.has_override || !!ov.unit_price || !!ov.description;
                              return (
                                <tr key={key} className={`border-b border-slate-100 ${hasOv ? "bg-amber-50" : ""}`}>
                                  <td className="p-1.5 font-mono">{l.date}</td>
                                  <td className="p-1.5">{l.committente_name}</td>
                                  <td className="p-1.5 font-mono">{l.intervention_ref}{(l.share_count || 1) > 1 ? ` (1/${l.share_count})` : ""}</td>
                                  <td className="p-1.5 font-mono">{r.code}</td>
                                  <td className="p-1.5">
                                    <Input
                                      defaultValue={r.description}
                                      key={`desc-${key}-${r.description}`}
                                      onBlur={(e) => {
                                        const v = e.target.value;
                                        if (v === r.original_description) resetRigaOverride(l.id, r.index);
                                        else setRigaOverride(l.id, r.index, { description: v });
                                      }}
                                      className="h-7 rounded-sm text-xs"
                                    />
                                  </td>
                                  <td className="p-1.5 text-right font-mono">{r.quantity}</td>
                                  <td className="p-1.5 text-right font-mono">
                                    <div className="flex items-center justify-end gap-1">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        defaultValue={Number(r.unit_price).toFixed(2)}
                                        key={`up-${key}-${r.unit_price}`}
                                        onBlur={(e) => {
                                          const v = Number(e.target.value);
                                          if (isNaN(v)) return;
                                          if (Math.abs(v - r.original_unit_price) < 0.005) resetRigaOverride(l.id, r.index);
                                          else setRigaOverride(l.id, r.index, { unit_price: v });
                                        }}
                                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                        className="h-7 w-24 rounded-sm text-right font-mono text-xs"
                                      />
                                      {hasOv && (
                                        <button
                                          onClick={() => resetRigaOverride(l.id, r.index)}
                                          className="text-slate-400 hover:text-red-600"
                                          title="Ripristina originale"
                                        >
                                          <RotateCcw className="h-3 w-3" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-1.5 text-right font-mono font-semibold">€ {Number(r.total).toFixed(2)}</td>
                                </tr>
                              );
                            }))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Addebiti */}
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Addebiti</div>
                        <div className="text-[11px] text-slate-400">{subSections.charges ? "Inclusi in PDF/Excel" : "Esclusi"}</div>
                      </div>
                      <table className="w-full text-xs">
                        <thead className="border-b border-slate-200 bg-slate-50 text-left font-bold uppercase tracking-widest text-slate-500">
                          <tr>
                            <th className="p-1.5">Data</th>
                            <th className="p-1.5">Descrizione</th>
                            <th className="p-1.5 text-right">Importo €</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(td.charges || []).length === 0 && <tr><td colSpan={3} className="p-4 text-center text-slate-400">Nessun addebito</td></tr>}
                          {(td.charges || []).map((c) => {
                            const ov = subChargeOv[c.id] || {};
                            const hasOv = c.has_override || ov.amount !== undefined || ov.description;
                            return (
                              <tr key={c.id} className={`border-b border-slate-100 ${hasOv ? "bg-amber-50" : ""}`}>
                                <td className="p-1.5 font-mono">{c.date}</td>
                                <td className="p-1.5">
                                  <Input
                                    defaultValue={c.description}
                                    key={`chg-desc-${c.id}-${c.description}`}
                                    onBlur={(e) => {
                                      const v = e.target.value;
                                      if (v === c.original_description) setChargeOverride(c.id, { description: null });
                                      else setChargeOverride(c.id, { description: v });
                                    }}
                                    className="h-7 rounded-sm text-xs"
                                  />
                                </td>
                                <td className="p-1.5 text-right font-mono">
                                  <div className="flex items-center justify-end gap-1">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      defaultValue={Number(c.amount).toFixed(2)}
                                      key={`chg-amt-${c.id}-${c.amount}`}
                                      onBlur={(e) => {
                                        const v = Number(e.target.value);
                                        if (isNaN(v)) return;
                                        if (Math.abs(v - c.original_amount) < 0.005) resetChargeOverride(c.id);
                                        else setChargeOverride(c.id, { amount: v });
                                      }}
                                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                      className="h-7 w-24 rounded-sm text-right font-mono text-xs"
                                    />
                                    {hasOv && (
                                      <button
                                        onClick={() => resetChargeOverride(c.id)}
                                        className="text-slate-400 hover:text-red-600"
                                        title="Ripristina originale"
                                      >
                                        <RotateCcw className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Materiale consegnato */}
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Materiale Consegnato</div>
                        <div className="text-[11px] text-slate-400">{subSections.supplies ? "Incluso in PDF/Excel" : "Escluso"}</div>
                      </div>
                      <table className="w-full text-xs">
                        <thead className="border-b border-slate-200 bg-slate-50 text-left font-bold uppercase tracking-widest text-slate-500">
                          <tr>
                            <th className="p-1.5 text-center">Addebita</th>
                            <th className="p-1.5">Data</th>
                            <th className="p-1.5">Materiale</th>
                            <th className="p-1.5 text-right">Q.tà</th>
                            <th className="p-1.5 text-right">Prezzo unit. €</th>
                            <th className="p-1.5 text-right">Totale</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(td.supplies || []).length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-400">Nessuna consegna</td></tr>}
                          {(td.supplies || []).map((s) => {
                            const ov = subSupplyOv[s.id] || {};
                            const hasOv = s.has_override || ov.unit_price !== undefined;
                            const charged = s.charged !== false;
                            return (
                              <tr key={s.id} className={`border-b border-slate-100 ${!charged ? "bg-slate-50 text-slate-400" : hasOv ? "bg-amber-50" : ""}`}>
                                <td className="p-1.5 text-center">
                                  <ChargeCheckbox id={s.id} charged={charged} disabled={!isAdmin} onToggle={() => toggleSupplyCharge(s.id, charged)} />
                                </td>
                                <td className="p-1.5 font-mono">{(s.date || "").slice(0, 10)}</td>
                                <td className="p-1.5">{s.material_name}{!charged && <NotChargedBadge />}</td>
                                <td className="p-1.5 text-right font-mono">{s.quantity}</td>
                                <td className="p-1.5 text-right font-mono">
                                  <div className="flex items-center justify-end gap-1">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      defaultValue={Number(s.unit_price).toFixed(2)}
                                      key={`sup-up-${s.id}-${s.unit_price}`}
                                      onBlur={(e) => {
                                        const v = Number(e.target.value);
                                        if (isNaN(v)) return;
                                        if (Math.abs(v - s.original_unit_price) < 0.005) resetSupplyOverride(s.id);
                                        else setSupplyOverride(s.id, { unit_price: v });
                                      }}
                                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                      className="h-7 w-24 rounded-sm text-right font-mono text-xs"
                                    />
                                    {hasOv && (
                                      <button
                                        onClick={() => resetSupplyOverride(s.id)}
                                        className="text-slate-400 hover:text-red-600"
                                        title="Ripristina originale"
                                      >
                                        <RotateCcw className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="p-1.5 text-right font-mono font-semibold">{charged ? `€ ${Number(s.total).toFixed(2)}` : <span className="line-through">€ {Number(s.original_total || 0).toFixed(2)}</span>}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            💡 Le modifiche a prezzi, importi e voci extra sono <b>salvate per questo BEF</b> (subappalto + periodo + committenti). Clicca <b>"Salva modifiche"</b> in alto per applicarle a PDF, Excel e Email.
          </div>
        </>
      )}

      {summary && !summary.is_subappalto && (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
            {[
              [t("auto.lordo_label"), `€ ${summary.totali.lordo.toFixed(2)}`, "text-slate-900"],
              [t("auto.sconto_percent_label", { percent: summary.totali.sconto_percent }), `- € ${summary.totali.sconto.toFixed(2)}`, "text-orange-600"],
              [t("auto.scontato_label"), `€ ${summary.totali.scontato.toFixed(2)}`, "text-slate-900"],
              [t("auto.addebiti_label"), `- € ${summary.totali.materiale.toFixed(2)}`, "text-red-600"],
              [
                summary.iva_applied ? t("auto.materiale_iva_label", { percent: summary.iva_percent }) : t("auto.materiale_consegnato_label"),
                `- € ${summary.totali.supplies_totale.toFixed(2)}`,
                "text-red-600",
              ],
              [t("auto.netto_uppercase"), `€ ${summary.totali.netto.toFixed(2)}`, "text-emerald-600"],
            ].map(([label, val, cls], i) => (
              <div key={i} className="kpi-card">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
                <div className={`mt-2 font-mono text-xl font-semibold ${cls}`}>{val}</div>
              </div>
            ))}
          </div>

          <div className="rounded-sm border border-slate-200 bg-white p-5">
            <div className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.lavorazioni")}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                  <tr><th className="p-2">{t("common.date")}</th><th className="p-2">{t("auto.committente_x")}</th><th className="p-2">{t("auto.impianto")}</th><th className="p-2">{t("auto.righe")}</th><th className="p-2 text-right">{t("common.total")}</th><th className="p-2 text-right">{t("auto.quota_tecnico")}</th><th className="p-2 text-right" title="Netto per questa lavorazione dopo lo sconto (editabile)">Netto post-sconto</th></tr>
                </thead>
                <tbody>
                  {summary.lavorazioni.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">{t("auto.nessuna_lavorazione_nel_periodo")}</td></tr>}
                  {summary.lavorazioni.map((l) => {
                    const isShared = (l.share_count || 1) > 1;
                    const hasOverride = !!l.has_override;
                    const hasNetOverride = !!l.has_net_override;
                    const displayTotal = Number(l.effective_total ?? l.total);
                    const netShare = Number(l.net_share ?? 0);
                    return (
                    <tr key={l.id} className={`border-b border-slate-100 ${hasOverride ? "bg-amber-50" : ""}`} data-testid={`bef-lav-${l.id}`}>
                      <td className="p-2 font-mono text-xs">{l.date}</td>
                      <td className="p-2">{l.committente_name}</td>
                      <td className="p-2 font-mono text-xs">
                        {l.intervention_ref || "—"}
                        {isShared && (
                          <>
                            <span className="ml-1 rounded-sm bg-blue-100 px-1 py-0.5 text-[9px] font-bold uppercase text-blue-800" title={`Diviso tra ${l.share_count} tecnici`}>
                              {l.is_primary_for_this_tech ? "P" : "C"}·1/{l.share_count}
                            </span>
                            {(l.shared_with || []).length > 0 && (
                              <div className="mt-0.5 text-[10px] font-normal not-italic text-blue-700" data-testid={`bef-shared-${l.id}`}>
                                Diviso con: <b>{(l.shared_with || []).join(", ")}</b>
                              </div>
                            )}
                          </>
                        )}
                        {hasOverride && (
                          <span className="ml-1 rounded-sm border border-amber-300 bg-amber-100 px-1 py-0.5 text-[9px] font-bold uppercase text-amber-800" title={`Originale: €${Number(l.total).toFixed(2)}`}>
                            Modificato
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-xs">{(l.righe || []).map((r) => `${r.quantity}× ${r.code}`).join(", ")}</td>
                      <td className={`p-2 text-right font-mono ${isShared ? "text-slate-500 line-through" : "font-semibold"}`}>
                        <button
                          type="button"
                          onClick={() => overrideLavorazioneTotal(l.id, displayTotal)}
                          className="rounded-sm px-1 hover:bg-blue-50 hover:text-[#0052FF]"
                          data-testid={`bef-lav-edit-${l.id}`}
                          title="Clicca per modificare l'importo consuntivato"
                        >
                          € {displayTotal.toFixed(2)}
                        </button>
                        {hasOverride && (
                          <button
                            type="button"
                            onClick={() => resetLavorazioneTotal(l.id)}
                            className="ml-1 text-[10px] font-bold text-amber-700 hover:text-amber-900"
                            data-testid={`bef-lav-reset-${l.id}`}
                            title="Ripristina somma righe"
                          >↺</button>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono font-semibold text-emerald-700">€ {Number(l.share ?? l.total).toFixed(2)}</td>
                      <td className={`p-2 text-right font-mono font-semibold ${hasNetOverride ? "bg-amber-50" : ""}`}>
                        <button
                          type="button"
                          onClick={() => overrideLavorazioneNet(l.id, netShare)}
                          className={`rounded-sm px-1 hover:bg-blue-50 hover:text-[#0052FF] ${hasNetOverride ? "text-amber-800" : "text-emerald-700"}`}
                          data-testid={`bef-lav-net-edit-${l.id}`}
                          title={hasNetOverride ? "Netto modificato manualmente (clicca per cambiare)" : "Clicca per fissare il netto post-sconto"}
                        >
                          € {netShare.toFixed(2)}
                        </button>
                        {hasNetOverride && (
                          <button
                            type="button"
                            onClick={() => resetLavorazioneNet(l.id)}
                            className="ml-1 text-[10px] font-bold text-amber-700 hover:text-amber-900"
                            data-testid={`bef-lav-net-reset-${l.id}`}
                            title="Ripristina netto calcolato (lordo × sconto)"
                          >↺</button>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-sm border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.materiale_consegnato_da_magazzino_viaggiante")}</div>
              <div className="text-xs text-slate-500">
                {t("auto.imponibile_prefix")} <span className="font-mono font-semibold text-slate-900">€ {summary.totali.supplies_imponibile.toFixed(2)}</span>
                {summary.iva_applied && (
                  <> · IVA {summary.iva_percent}%: <span className="font-mono font-semibold text-slate-900">€ {summary.totali.supplies_iva.toFixed(2)}</span></>
                )}
                {" · "}{t("auto.totale_prefix")} <span className="font-mono font-semibold text-red-600">€ {summary.totali.supplies_totale.toFixed(2)}</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-2 text-center">Addebita</th>
                    <th className="p-2">{t("common.date")}</th>
                    <th className="p-2">{t("common.material")}</th>
                    <th className="p-2 text-right">{t("auto.qtà")}</th>
                    <th className="p-2 text-right">{t("auto.prezzo_unit")}</th>
                    <th className="p-2 text-right">{t("auto.imponibile")}</th>
                    <th className="p-2 text-center">{t("auto.iva_x")}</th>
                    <th className="p-2 text-right">IVA {summary.iva_percent}%</th>
                    <th className="p-2 text-right">{t("common.total")}</th>
                    <th className="p-2">{t("common.notes")}</th>
                    {isAdmin && <th className="p-2" />}
                  </tr>
                </thead>
                <tbody>
                  {(!summary.supplies || summary.supplies.length === 0) && (
                    <tr><td colSpan={11} className="p-6 text-center text-slate-400">{t("auto.nessun_materiale_consegnato_nel_periodo")}</td></tr>
                  )}
                  {(summary.supplies || []).map((s) => {
                    const isOverridden = !!s.price_override;
                    const rowIva = Number(s.iva_amount || 0);
                    const effIva = !!s.apply_iva_effective;
                    const charged = s.charged !== false;
                    return (
                      <tr key={s.id} className={`border-b border-slate-100 ${!charged ? "bg-slate-50 text-slate-400" : isOverridden ? "bg-amber-50" : ""}`} data-testid={`bef-supply-${s.id}`}>
                        <td className="p-2 text-center">
                          <ChargeCheckbox id={s.id} charged={charged} disabled={!isAdmin} onToggle={() => toggleSupplyCharge(s.id, charged)} />
                        </td>
                        <td className="p-2 font-mono text-xs">{(s.date || "").slice(0, 10)}</td>
                        <td className="p-2">
                          {s.material_name}
                          {!charged && <NotChargedBadge />}
                          {isOverridden && <span className="ml-2 rounded-sm border border-amber-300 bg-amber-100 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800">{t("auto.prezzo_modificato")}</span>}
                        </td>
                        <td className="p-2 text-right font-mono">{s.quantity}</td>
                        <td className="p-2 text-right font-mono">
                          {isAdmin ? (
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-slate-400">€</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                defaultValue={Number(s.unit_price).toFixed(2)}
                                key={`price-${s.id}-${s.unit_price}`}
                                onBlur={(e) => {
                                  const v = Number(e.target.value);
                                  if (isNaN(v) || v < 0) return;
                                  if (Math.abs(v - Number(s.unit_price)) < 0.005) return;
                                  saveSupplyPrice(s.id, v);
                                }}
                                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                className="h-7 w-24 rounded-sm text-right font-mono text-xs"
                                data-testid={`bef-supply-price-${s.id}`}
                                title={t("auto.modifica_il_prezzo_unitario_di_questa_consegna_persistente_n")}
                              />
                              {isOverridden && (
                                <button
                                  onClick={() => resetSupplyPrice(s.id)}
                                  className="text-slate-400 hover:text-red-600"
                                  title={t("auto.ripristina_prezzo_listino")}
                                  data-testid={`bef-supply-reset-${s.id}`}
                                >
                                  <RotateCcw className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <>€ {Number(s.unit_price).toFixed(2)}</>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono">{charged ? `€ ${Number(s.total).toFixed(2)}` : <span className="line-through">€ {Number(s.original_total || 0).toFixed(2)}</span>}</td>
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={effIva}
                            disabled={!isAdmin}
                            onChange={() => isAdmin && toggleSupplyIva(s.id, effIva)}
                            className="h-4 w-4 rounded-sm accent-[#0052FF]"
                            data-testid={`bef-supply-iva-${s.id}`}
                            title={isAdmin ? "Applica IVA a questa riga" : ""}
                          />
                        </td>
                        <td className="p-2 text-right font-mono text-slate-600">{effIva ? `€ ${rowIva.toFixed(2)}` : "—"}</td>
                        <td className="p-2 text-right font-mono font-semibold">€ {(Number(s.total) + rowIva).toFixed(2)}</td>
                        <td className="p-2 text-xs text-slate-500">{s.notes || "—"}</td>
                        {isAdmin && (
                          <td className="p-2 text-right">
                            <DeleteRowBtn onClick={() => deleteSupply(s)} testid={`bef-supply-delete-${s.id}`} />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              I materiali qui elencati vengono automaticamente aggiunti al BEF dal <b>{t("auto.magazzino_viaggiante")}</b> del tecnico.
              {isAdmin && <> Puoi <b>{t("auto.modificare_il_prezzo_unitario")}</b> di una singola consegna cliccando sulla cella: la modifica è <b>{t("auto.salvata_in_modo_permanente_su_quella_riga")}</b> (visibile anche nei BEF futuri) ma <b>{t("auto.non_tocca_il_listino_globale")}</b>.</>}
            </div>
          </div>

          <div className="rounded-sm border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.materiali_addebitati_kicker")}</div>
              <Dialog open={chgOpen} onOpenChange={setChgOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="rounded-sm" data-testid="btn-add-charge"><Plus className="mr-1 h-3 w-3" /> {t("auto.aggiungi_addebito_btn")}</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t("auto.nuovo_addebito_materiale")}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>{t("auto.materiale_opz_precompila")}</Label>
                      <Select value={chg.material_id || "__none__"} onValueChange={(v) => pickMaterial(v === "__none__" ? "" : v)}>
                        <SelectTrigger className="rounded-sm" data-testid="charge-material"><SelectValue placeholder={t("auto.scegli_dall_anagrafica")} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t("auto.nessuno_voce_libera")}</SelectItem>
                          {materials.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.name} — € {Number(m.unit_price ?? 0).toFixed(2)}/{m.unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {chg.material_id && (
                        <div className="mt-1 text-[10px] text-slate-500">
                          {t("auto.prezzo_importato_hint")}
                        </div>
                      )}
                    </div>
                    <div><Label>{t("common.date")}</Label><Input type="date" value={chg.date} onChange={(e) => setChg({ ...chg, date: e.target.value })} className="rounded-sm" /></div>
                    <div><Label>{t("common.description")}</Label><Input value={chg.description} onChange={(e) => setChg({ ...chg, description: e.target.value })} className="rounded-sm" data-testid="charge-desc" /></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>{t("common.quantity")}</Label>
                        <Input type="number" step="0.01" min="0" value={chg.quantity} onChange={(e) => updateQty(e.target.value)} className="rounded-sm font-mono" data-testid="charge-qty" />
                      </div>
                      <div>
                        <Label>{t("auto.prezzo_unit_eur")}</Label>
                        <Input type="number" step="0.01" min="0" value={chg.unit_price} onChange={(e) => updateUnitPrice(e.target.value)} className="rounded-sm font-mono" data-testid="charge-unit-price" />
                      </div>
                      <div>
                        <Label>{t("auto.importo_totale_eur")}</Label>
                        <Input type="number" step="0.01" value={chg.amount} onChange={(e) => setChg({ ...chg, amount: e.target.value })} className="rounded-sm bg-slate-50 font-mono font-semibold" data-testid="charge-amount" />
                      </div>
                    </div>
                    <div><Label>{t("common.notes")}</Label><Input value={chg.notes} onChange={(e) => setChg({ ...chg, notes: e.target.value })} className="rounded-sm" /></div>
                  </div>
                  <DialogFooter><Button onClick={addCharge} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="charge-save">{t("common.save")}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                <tr><th className="p-2">{t("common.date")}</th><th className="p-2">{t("common.description")}</th><th className="p-2">{t("common.notes")}</th><th className="p-2 text-right">{t("auto.importo_col")}</th>{isAdmin && <th className="p-2" />}</tr>
              </thead>
              <tbody>
                {summary.charges.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">{t("auto.nessun_addebito")}</td></tr>}
                {summary.charges.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100" data-testid={`bef-charge-row-${c.id}`}>
                    <td className="p-2 font-mono text-xs">{c.date}</td>
                    <td className="p-2">{c.description}</td>
                    <td className="p-2 text-xs text-slate-500">{c.notes || "—"}</td>
                    <td className="p-2 text-right font-mono">€ {Number(c.amount).toFixed(2)}</td>
                    {isAdmin && (
                      <td className="p-2 text-right">
                        <DeleteRowBtn onClick={() => deleteCharge(c)} testid={`bef-charge-delete-${c.id}`} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Dialog Invio Email BEF */}
      <Dialog open={emailOpen} onOpenChange={(o) => { if (!o && !emailBusy) setEmailOpen(false); }}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-600" /> Invia Benestare via Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-sm bg-slate-50 p-3 text-xs">
              <div className="mb-1"><b>Collaboratore:</b> {technicians.find((x) => x.id === tech)?.name || "—"}</div>
              <div className="mb-1"><b>Oggetto:</b> Benestare lavorazioni {competenzaLabel}</div>
              <div><b>Allegato:</b> BEF_{(technicians.find((x) => x.id === tech)?.name || "collaboratore").replace(/\s+/g, "_")}_{dateFrom}_{dateTo}.pdf</div>
            </div>
            <div>
              <Label className="text-xs">Destinatario *</Label>
              <Input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="collaboratore@..."
                className="rounded-sm"
                data-testid="bef-email-to"
              />
              <div className="mt-1 text-[11px] text-slate-500">
                Prefilled con l'email del collaboratore. Puoi modificarla.
              </div>
            </div>
            {smtpAccounts.length > 0 && (
              <div>
                <Label className="text-xs">Account SMTP</Label>
                <Select value={smtpAccountId || smtpAccounts[0]?.id || ""} onValueChange={setSmtpAccountId}>
                  <SelectTrigger className="rounded-sm" data-testid="bef-email-smtp"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {smtpAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.label} — {a.from_email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {smtpAccounts.length === 0 && (
              <div className="rounded-sm border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                ⚠️ Nessun account SMTP configurato. Vai su <b>Impostazioni → Email SMTP</b> per aggiungerne uno.
                L'allegato PDF richiede un mittente SMTP aziendale.
              </div>
            )}
            <div className="rounded-sm border border-slate-200 bg-white p-3 text-xs text-slate-700">
              <div className="mb-1 font-bold uppercase tracking-wider text-slate-500">Anteprima corpo email</div>
              <div>Buongiorno {technicians.find((x) => x.id === tech)?.name || "…"},</div>
              <div className="my-1">in allegato quanto in oggetto.</div>
              <div>Cordiali saluti</div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={emailBusy} className="rounded-sm">Annulla</Button>
            <Button
              onClick={sendBefEmail}
              disabled={emailBusy || !emailTo.includes("@") || smtpAccounts.length === 0}
              className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="bef-email-send"
            >
              <Send className="mr-2 h-4 w-4" />
              {emailBusy ? "Invio…" : "Invia Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChargeCheckbox({ id, charged, disabled, onToggle }) {
  return (
    <input
      type="checkbox"
      checked={charged}
      disabled={disabled}
      onChange={() => !disabled && onToggle()}
      className="h-4 w-4 rounded-sm accent-emerald-600"
      data-testid={`bef-supply-charge-${id}`}
      title={charged ? "Addebitato: togli il flag per non addebitarlo" : "Non addebitato: metti il flag per addebitarlo"}
    />
  );
}

function NotChargedBadge() {
  return <span className="ml-2 rounded-sm border border-slate-300 bg-white px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">Non addebitato</span>;
}

